import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, TrendingUp, ChevronDown, ChevronUp, Info } from 'lucide-react';
import { useTrialSummary } from '../../hooks/useBilling';
import { useOrganization } from '../../hooks/useOrganization';

export const TrialBanner: React.FC = () => {
    const { data: organization } = useOrganization();
    const [isExpanded, setIsExpanded] = useState(false);

    // Check if user is on a free plan (no trial countdown)
    const isOnFreePlan = organization?.plan?.code === 'free' || organization?.free_plan?.is_on_free_plan;
    
    // Only fetch trial summary if user is on trial (backward compatibility)
    const isOnTrial = organization?.trial?.is_on_trial || organization?.is_on_trial;
    
    // Always call hooks, but conditionally enable
    const { data, isLoading } = useTrialSummary(!!isOnTrial && !isOnFreePlan);

    // Don't show for free plan users (no trial/countdown)
    if (isOnFreePlan) return null;

    // Don't show if not on trial or loading organization data
    if (!isOnTrial || isLoading || !data) return null;

    // Don't show if trial expired
    if (data.trial.is_expired) return null;

    const isUrgent = data.trial.days_remaining <= 2;

    return (
        <div className={`border-l-4 rounded-r-lg mb-6 ${isUrgent
            ? 'bg-red-50 border-red-400'
            : 'bg-yellow-50 border-yellow-400'
            }`}>
            {/* Collapsed Header - Always Visible */}
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full p-3 flex items-center justify-between hover:bg-opacity-80 transition-colors"
            >
                <div className="flex items-center gap-3">
                    <div className="flex-shrink-0">
                        {isUrgent ? (
                            <AlertTriangle className="w-5 h-5 text-red-600" />
                        ) : (
                            <Info className="w-5 h-5 text-yellow-600" />
                        )}
                    </div>
                    <div className="text-left">
                        <p className={`text-sm font-semibold ${isUrgent ? 'text-red-800' : 'text-yellow-800'
                            }`}>
                            {isUrgent ? '🚨 ' : '⏰ '}
                            Trial ends in <strong>{data.trial.days_remaining} day{data.trial.days_remaining !== 1 ? 's' : ''}</strong>
                            {data.value_demonstration.potential_loss_prevented > 0 && (
                                <span className="ml-2">
                                    · ${data.value_demonstration.potential_loss_prevented.toLocaleString()} saved
                                </span>
                            )}
                        </p>
                    </div>
                </div>
                <div className="flex-shrink-0">
                    {isExpanded ? (
                        <ChevronUp className={`w-5 h-5 ${isUrgent ? 'text-red-600' : 'text-yellow-600'}`} />
                    ) : (
                        <ChevronDown className={`w-5 h-5 ${isUrgent ? 'text-red-600' : 'text-yellow-600'}`} />
                    )}
                </div>
            </button>

            {/* Expanded Content */}
            {isExpanded && (
                <div className={`px-3 pb-4 border-t ${isUrgent ? 'border-red-200' : 'border-yellow-200'
                    }`}>
                    <div className="flex items-start gap-3 pt-3">
                        <div className="flex-shrink-0">
                            <TrendingUp className={`w-5 h-5 ${isUrgent ? 'text-red-600' : 'text-yellow-600'
                                }`} />
                        </div>
                        <div className="flex-1">
                            <p className={`text-sm font-semibold ${isUrgent ? 'text-red-800' : 'text-yellow-800'
                                }`}>
                                {data.risk_summary.message}
                            </p>
                            <p className={`mt-1 text-sm ${isUrgent ? 'text-red-700' : 'text-yellow-700'
                                }`}>
                                Potential loss prevented: <strong>${data.value_demonstration.potential_loss_prevented.toLocaleString()}</strong>
                            </p>
                            <p className={`mt-2 text-sm ${isUrgent ? 'text-red-700' : 'text-yellow-700'
                                }`}>
                                <Link
                                    to="/pricing"
                                    className={`font-medium underline hover:no-underline ${isUrgent ? 'text-red-900' : 'text-yellow-900'
                                        }`}
                                >
                                    {data.value_demonstration.upgrade_cta} →
                                </Link>
                            </p>
                            {data.upgrade.benefits && data.upgrade.benefits.length > 0 && (
                                <ul className={`mt-2 text-xs space-y-1 ${isUrgent ? 'text-red-600' : 'text-yellow-600'
                                    }`}>
                                    {data.upgrade.benefits.slice(0, 3).map((benefit: string, idx: number) => (
                                        <li key={idx}>✓ {benefit}</li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
